import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  InstagramLogo,
  FacebookLogo,
  LinkedinLogo,
  XLogo,
  YoutubeLogo,
  TiktokLogo,
  type Icon,
} from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { LanguagePicker } from "./LanguagePicker";
import type { I18nText, FooterConfig, SocialPlatform } from "@/types/footer";

const SOCIAL_ICONS: Record<SocialPlatform, { Icon: Icon; label: string }> = {
  instagram: { Icon: InstagramLogo, label: "Instagram" },
  facebook: { Icon: FacebookLogo, label: "Facebook" },
  linkedin: { Icon: LinkedinLogo, label: "LinkedIn" },
  x: { Icon: XLogo, label: "X" },
  youtube: { Icon: YoutubeLogo, label: "YouTube" },
  tiktok: { Icon: TiktokLogo, label: "TikTok" },
};

function isInternalRoute(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//");
}

function safeHref(href: string): string {
  if (/^(\/[^/]|\/(?:$)|#|https?:\/\/|mailto:)/i.test(href)) return href;
  return "#";
}

function getText(text: I18nText, locale: string): string {
  return (text as Record<string, string>)[locale] || text.bs || text.en || "";
}

export function Footer() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const { data: config } = useQuery({
    queryKey: ["footer-config"],
    queryFn: async () => {
      const res = await fetch("/api/footer");
      if (!res.ok) throw new Error("Failed to fetch footer config");
      return res.json() as Promise<FooterConfig>;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (!config) {
    return (
      <footer
        style={{
          background:
            "linear-gradient(180deg, oklch(96% 0.012 84), oklch(93.5% 0.016 84))",
          borderTop: "1px solid oklch(90% 0.02 84 / 0.6)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 pt-12 pb-6 sm:px-6 lg:px-8">
          <div className="h-32" />
        </div>
      </footer>
    );
  }

  const copyrightText = getText(config.copyright, locale).replace(
    "{year}",
    String(new Date().getFullYear())
  );

  const socials = (config.socials ?? []).filter((s) => s.url && SOCIAL_ICONS[s.platform]);

  return (
    <footer
      style={{
        background:
          "linear-gradient(180deg, oklch(96% 0.012 84), oklch(93.5% 0.016 84))",
        borderTop: "1px solid oklch(90% 0.02 84 / 0.6)",
      }}
    >
      <div className="mx-auto max-w-7xl px-4 pt-12 pb-6 sm:px-6 lg:px-8">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <Logo
              className="h-6 w-auto"
              style={{ color: "oklch(25% 0.05 84)" }}
            />

            <p
              className="mt-3 max-w-[280px] text-sm leading-relaxed"
              style={{ color: "oklch(48% 0.025 84)" }}
            >
              {getText(config.description, locale)}
            </p>

            {socials.length > 0 && (
              <div className="mt-4 flex gap-2">
                {socials.map((social) => {
                  const { Icon, label } = SOCIAL_ICONS[social.platform];
                  return (
                    <a
                      key={social.platform}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      title={label}
                      className="inline-flex size-8 items-center justify-center rounded-full transition-colors"
                      style={{
                        color: "oklch(45% 0.03 84)",
                        background: "oklch(100% 0 0 / 0.7)",
                        border: "1px solid oklch(90% 0.02 84 / 0.7)",
                      }}
                    >
                      <Icon className="size-[18px]" weight="regular" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Link columns */}
          {config.columns.map((col, idx) => (
            <div key={idx}>
              <h4
                className="text-sm font-semibold"
                style={{ color: "oklch(30% 0.04 84)" }}
              >
                {getText(col.title, locale)}
              </h4>
              <ul className="mt-3 space-y-2">
                {col.links.map((link, linkIdx) => {
                  const label = getText(link.label, locale);
                  const href = safeHref(link.href);
                  if (!label) return null;
                  return (
                    <li key={linkIdx}>
                      {isInternalRoute(href) ? (
                        <Link
                          to={href}
                          className="text-sm transition-colors hover:text-[oklch(25%_0.05_84)]"
                          style={{ color: "oklch(48% 0.025 84)" }}
                        >
                          {label}
                        </Link>
                      ) : (
                        <a
                          href={href}
                          className="text-sm transition-colors hover:text-[oklch(25%_0.05_84)]"
                          style={{ color: "oklch(48% 0.025 84)" }}
                        >
                          {label}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Point-of-sale legal info (required by the card processor) */}
          {config.merchant && config.merchant.lines.length > 0 && (
            <div>
              <h4
                className="text-sm font-semibold"
                style={{ color: "oklch(30% 0.04 84)" }}
              >
                {getText(config.merchant.title, locale)}
              </h4>
              <ul className="mt-3 space-y-2">
                {config.merchant.lines.map((line, lineIdx) => {
                  const text = getText(line, locale);
                  if (!text) return null;
                  return (
                    <li
                      key={lineIdx}
                      className="text-sm leading-relaxed"
                      style={{ color: "oklch(48% 0.025 84)" }}
                    >
                      {text}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div
          className="mt-10 flex flex-col-reverse items-center justify-between gap-4 border-t pt-6 text-xs sm:flex-row"
          style={{
            borderColor: "oklch(90% 0.02 84 / 0.6)",
            color: "oklch(55% 0.02 84)",
          }}
        >
          <div className="text-center sm:text-left">{copyrightText}</div>
          <LanguagePicker variant="footer" align="right" />
        </div>
      </div>
    </footer>
  );
}
